import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { checkCircle, fileCheck, uploadCloud } from '@/assets/icons';
import { ImportMatchedSongs } from '@/components/Layout/components/ImportMatchedSongs';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { Loader } from '@/components/shared/Loader';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { dashboardUrl, songsUrl } from '@/config/urls';
import {
	SongImportColumnAliasesInput,
	useCancelSongsImportUploadMutation,
	useGetSongsImportQuery,
	useProcessSongsImportUploadMutation,
	useReadSongsImportUploadMutation,
	useRequestSongImportUploadMutation,
	useVerifySongsImportUploadMutation,
} from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { classNames } from '@/helpers/classNames';
import { getAcronym } from '@/helpers/textTransform';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { CircularProgress, SvgIcon } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';

import styles from './ImportCsv.module.scss';

/** Paste a public URL later once the template is uploaded. */
export const SONG_IMPORT_SAMPLE_CSV_URL =
	'https://asaph-public.s3.ap-southeast-2.amazonaws.com/song-import-template.csv';

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const MAX_ONLY_SONG_IDS = 5000;
const FLOW_STEPS = 3;

type ColumnAliasKey = keyof SongImportColumnAliasesInput;

type ColumnAliasesState = Record<ColumnAliasKey, string | null>;

type VerifyReport = {
	totalSongs: number;
	matchedSongIds: string[];
	existingOnTeam: string[];
	newSongs: string[];
	missingLyrics: string[];
	sample: Array<{
		title: string;
		artist: string;
		defaultKey?: string | null;
		artworkUrl?: string | null;
	}>;
};

const EMPTY_ALIASES: ColumnAliasesState = {
	title: null,
	artist: null,
	lyrics: null,
	defaultKey: null,
	energy: null,
	ccli: null,
	spotifyUrl: null,
	youtubeUrl: null,
};

const FIELD_META: Array<{
	key: ColumnAliasKey;
	label: string;
	/** Preferred CSV header names (order = priority). Keep in sync with server SONG_IMPORT_COLUMN_ALIASES. */
	columnAliases: string[];
	section: 'required' | 'recommended' | 'optional';
}> = [
	{
		key: 'title',
		label: 'Title',
		columnAliases: ['title'],
		section: 'required',
	},
	{
		key: 'artist',
		label: 'Artist',
		columnAliases: ['artist'],
		section: 'required',
	},
	{
		key: 'defaultKey',
		label: 'Default Key',
		columnAliases: ['default key', 'default_key', 'default-key'],
		section: 'recommended',
	},
	{
		key: 'energy',
		label: 'Energy',
		columnAliases: ['energy'],
		section: 'recommended',
	},
	{
		key: 'lyrics',
		label: 'Lyrics',
		columnAliases: ['lyrics'],
		section: 'optional',
	},
	{
		key: 'ccli',
		label: 'CCLI No.',
		columnAliases: ['ccli no.', 'ccli_no', 'ccli'],
		section: 'optional',
	},
	{
		key: 'spotifyUrl',
		label: 'Spotify URL',
		columnAliases: ['spotify url', 'spotify_url', 'spotify-url'],
		section: 'optional',
	},
	{
		key: 'youtubeUrl',
		label: 'YouTube URL',
		columnAliases: ['youtube url', 'youtube_url', 'youtube-url'],
		section: 'optional',
	},
];

const findColumn = (
	columns: string[],
	aliases: string[],
): string | null => {
	for (const preferred of aliases) {
		const exact = columns.find((c) => c === preferred);
		if (exact) return exact;
		const ci = columns.find(
			(c) => c.toLowerCase() === preferred.toLowerCase(),
		);
		if (ci) return ci;
	}
	return null;
};

const buildAliasesFromColumns = (columns: string[]): ColumnAliasesState => {
	const next = { ...EMPTY_ALIASES };
	for (const field of FIELD_META) {
		next[field.key] = findColumn(columns, field.columnAliases);
	}
	return next;
};

const toSettingsInput = (aliases: ColumnAliasesState) => ({
	columnAliases: {
		// Empty string = explicitly unmapped (server skips fallbacks for that field).
		title: aliases.title ?? '',
		artist: aliases.artist ?? '',
		lyrics: aliases.lyrics ?? '',
		defaultKey: aliases.defaultKey ?? '',
		energy: aliases.energy ?? '',
		ccli: aliases.ccli ?? '',
		spotifyUrl: aliases.spotifyUrl ?? '',
		youtubeUrl: aliases.youtubeUrl ?? '',
	},
});

const listBottomCardContent = (items: string[]) => (
	<div>
		{items.map((item) => (
			<div key={item} className={styles['list-item']}>
				{item}
			</div>
		))}
	</div>
);

export type ImportCsvProps = {
	onClose: () => void;
};

export const ImportCsv: React.FC<ImportCsvProps> = ({ onClose }) => {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();
	const { openBottomCard, openSong } = useLayout();
	const { refetch: refetchTeamSongs } = useTeamSongs();

	const fireOpen = useAnalyticsEvent('Import_CSV_Open');
	const fireClose = useAnalyticsEvent('Import_CSV_Close');
	const fireLearnMore = useAnalyticsEvent('Import_CSV_Learn_More');
	const fireSelectFile = useAnalyticsEvent('Import_CSV_Select_File');
	const fireMapColumns = useAnalyticsEvent('Import_CSV_Map_Columns');
	const fireVerify = useAnalyticsEvent('Import_CSV_Verify');
	const fireViewMatched = useAnalyticsEvent('Import_CSV_View_Matched');
	const fireViewExisting = useAnalyticsEvent('Import_CSV_View_Existing');
	const fireViewNew = useAnalyticsEvent('Import_CSV_View_New_Songs');
	const fireViewMissing = useAnalyticsEvent('Import_CSV_View_Missing_Lyrics');
	const fireStart = useAnalyticsEvent('Import_CSV_Start');
	const fireDoneHome = useAnalyticsEvent('Import_CSV_Done_Home');
	const fireDoneSongs = useAnalyticsEvent('Import_CSV_Done_Songs');

	const [step, setStep] = useState(0);
	const [file, setFile] = useState<File | null>(null);
	const [uploadKey, setUploadKey] = useState<string | null>(null);
	const [columns, setColumns] = useState<string[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [aliases, setAliases] = useState<ColumnAliasesState>(EMPTY_ALIASES);
	const [report, setReport] = useState<VerifyReport | null>(null);
	const [uploading, setUploading] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [verifyJobActive, setVerifyJobActive] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [matchedViewerIds, setMatchedViewerIds] = useState<string[] | null>(
		null,
	);
	const [mappingMenu, setMappingMenu] = useState<{
		key: ColumnAliasKey;
		anchor: HTMLElement;
	} | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const openedRef = useRef(false);

	const { mutateAsync: requestUpload } =
		useRequestSongImportUploadMutation();
	const { mutateAsync: readUpload } = useReadSongsImportUploadMutation();
	const { mutateAsync: verifyUpload } = useVerifySongsImportUploadMutation();
	const { mutateAsync: processUpload } =
		useProcessSongsImportUploadMutation();
	const { mutateAsync: cancelImport } = useCancelSongsImportUploadMutation();

	const { data: importStatus, refetch: refetchImportStatus } =
		useGetSongsImportQuery(
			{ uploadKey: uploadKey || '' },
			{
				enabled:
					!!uploadKey &&
					(verifyJobActive || step === 3 || step === 4),
				refetchInterval: verifyJobActive || step === 3 ? 2000 : false,
				retry: false,
			},
		);

	useEffect(() => {
		if (!openedRef.current) {
			openedRef.current = true;
			fireOpen();
		}
	}, [fireOpen]);

	const importProgress = importStatus?.getSongsImport;
	const importDone =
		importProgress?.phase === 'import' &&
		(!!importProgress?.completedAt || !!importProgress?.error);

	useEffect(() => {
		if (step === 3 && importDone) {
			setStep(4);
			setProcessing(false);
			if (!importProgress?.error) {
				refetchTeamSongs();
			}
		}
	}, [step, importDone, importProgress?.error, refetchTeamSongs]);

	useEffect(() => {
		if (!verifyJobActive || !importProgress) return;
		if (importProgress.phase !== 'verify') return;
		if (
			!importProgress.completedAt &&
			!importProgress.error &&
			!importProgress.cancelledAt
		) {
			return;
		}

		setVerifyJobActive(false);
		setVerifying(false);

		if (importProgress.cancelledAt) {
			return;
		}
		if (importProgress.error) {
			handleError({
				error: new Error(importProgress.error),
				errorCode: 'ImportCsvVerifyError',
				friendlyMessage:
					'Failed to verify the CSV. Check column mapping and try again.',
			});
			return;
		}
		if (importProgress.report) {
			setReport(importProgress.report);
			setStep(2);
		}
	}, [verifyJobActive, importProgress, handleError]);

	const canContinueMapping = !!aliases.title && !!aliases.artist;
	const importableCount = report
		? report.matchedSongIds.length + report.newSongs.length
		: 0;

	const columnSelectItems = useMemo(
		() => [
			{ label: 'Not mapped', value: '' },
			...columns.map((c) => ({ label: c, value: c })),
		],
		[columns],
	);

	const resetFile = useCallback(() => {
		setFile(null);
		setUploadKey(null);
		setColumns([]);
		setRowCount(0);
		setAliases(EMPTY_ALIASES);
		setReport(null);
		setVerifyJobActive(false);
		setVerifying(false);
		setStep(0);
	}, []);

	const handleClose = () => {
		fireClose({ step });
		// Verify and import both run fire-and-forget on the server — cancel via
		// the shared mutation. Best-effort: ignore 404 if the job already finished.
		if ((verifyJobActive || step === 3) && uploadKey) {
			void cancelImport({ uploadKey }).catch(() => {});
		}
		setVerifyJobActive(false);
		setVerifying(false);
		onClose();
	};

	const uploadSelectedFile = async (selected: File) => {
		if (selected.size > MAX_UPLOAD_BYTES) {
			enqueueSnackbar('CSV file exceeds the 50MB size limit', {
				variant: 'warning',
			});
			return;
		}

		setUploading(true);
		fireSelectFile();
		try {
			const { requestSongImportUpload } = await requestUpload({});
			const { key, uploadUrl } = requestSongImportUpload;

			const putRes = await fetch(uploadUrl, {
				method: 'PUT',
				headers: { 'Content-Type': 'text/csv' },
				body: selected,
			});
			if (!putRes.ok) {
				throw new Error(`Upload failed with status ${putRes.status}`);
			}

			const { readSongsImportUpload } = await readUpload({
				uploadKey: key,
			});

			setFile(selected);
			setUploadKey(key);
			setColumns(readSongsImportUpload.columns);
			setRowCount(readSongsImportUpload.rowCount);
			setAliases(buildAliasesFromColumns(readSongsImportUpload.columns));
		} catch (error) {
			handleError({
				error,
				errorCode: 'ImportCsvUploadError',
				friendlyMessage: 'Failed to upload CSV. Please try again.',
			});
			resetFile();
		} finally {
			setUploading(false);
		}
	};

	const onFilePicked = (selected: File | null | undefined) => {
		if (!selected) return;
		void uploadSelectedFile(selected);
	};

	const runVerify = async () => {
		if (!uploadKey || !canContinueMapping || verifying) return;

		setVerifying(true);
		fireMapColumns();
		fireVerify();
		try {
			// Starts a background verify job; completion is observed via polling.
			await verifyUpload({
				uploadKey,
				settings: toSettingsInput(aliases),
			});
			setVerifyJobActive(true);
			void refetchImportStatus();
		} catch (error) {
			setVerifying(false);
			handleError({
				error,
				errorCode: 'ImportCsvVerifyError',
				friendlyMessage:
					'Failed to verify the CSV. Check column mapping and try again.',
			});
		}
	};

	const startImport = async () => {
		if (!uploadKey || !report || importableCount === 0) return;
		setProcessing(true);
		fireStart();
		try {
			await processUpload({
				uploadKey,
				settings: toSettingsInput(aliases),
			});
			setStep(3);
			void refetchImportStatus();
		} catch (error) {
			setProcessing(false);
			handleError({
				error,
				errorCode: 'ImportCsvProcessError',
				friendlyMessage:
					'Failed to start the import. Please try again.',
			});
		}
	};

	const openMatchedSongs = () => {
		if (!report?.matchedSongIds.length) return;
		fireViewMatched();
		let ids = report.matchedSongIds;
		if (ids.length > MAX_ONLY_SONG_IDS) {
			enqueueSnackbar(
				`Showing the first ${MAX_ONLY_SONG_IDS} of ${ids.length} matched songs`,
				{ variant: 'warning' },
			);
			ids = ids.slice(0, MAX_ONLY_SONG_IDS);
		}
		setMatchedViewerIds(ids);
	};

	const openListCard = (
		title: string,
		items: string[],
		event: 'existing' | 'new' | 'missing',
	) => {
		if (!items.length) return;
		if (event === 'existing') fireViewExisting();
		if (event === 'new') fireViewNew();
		if (event === 'missing') fireViewMissing();
		openBottomCard({
			title,
			text: `${items.length} song${items.length === 1 ? '' : 's'}`,
			component: listBottomCardContent(items),
		});
	};

	const openLearnMore = () => {
		fireLearnMore();
		openBottomCard({
			title: 'How CSV import works',
			text: 'Upload a CSV using our template or your own. We’ll help map the columns. We’ll match songs from the Asaph song library where we can, and create the rest as new ones. Everything ends up in your Asaph team library so you and your team can access it. There are multiple steps to review your upload before the final confirmation.',
		});
	};

	const goDashboard = () => {
		fireDoneHome();
		onClose();
		navigate(dashboardUrl);
	};

	const goSongs = () => {
		fireDoneSongs();
		onClose();
		navigate(songsUrl);
	};

	const progressPercent =
		step === 4
			? 100
			: step === 3
			? Math.max(importProgress?.progress ?? 8, 8)
			: 0;

	const renderUploadStep = () => (
		<div className={styles['step']}>
			<div className={styles['heading-block']}>
				<div className={styles['title']}>Upload a CSV file</div>
				<div className={styles['subtitle']}>
					{SONG_IMPORT_SAMPLE_CSV_URL ? (
						<a
							href={SONG_IMPORT_SAMPLE_CSV_URL}
							target="_blank"
							rel="noreferrer"
						>
							Download a sample CSV file
						</a>
					) : (
						'Download a sample CSV file'
					)}{' '}
					or{' '}
					<span
						className={styles['link']}
						onClick={openLearnMore}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter') openLearnMore();
						}}
					>
						learn more
					</span>{' '}
					about importing songs.
				</div>
			</div>

			{file ? (
				<div className={styles['file-selected']}>
					<SvgIcon
						component={fileCheck}
						viewBox="0 0 48 48"
						className={classNames([
							styles['file-icon'],
							styles['no-fill'],
						])}
					/>
					<div className={styles['filename']}>{file.name}</div>
					<div className={styles['remove-line']}>
						Changed your mind?{' '}
						<span
							className={styles['remove']}
							onClick={resetFile}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === 'Enter') resetFile();
							}}
						>
							Remove CSV
						</span>
					</div>
					{rowCount > 0 && (
						<div className={styles['row-count']}>
							{rowCount} row{rowCount === 1 ? '' : 's'} detected
						</div>
					)}
				</div>
			) : (
				<div
					className={classNames([
						styles['dropzone'],
						dragging && styles['dragging'],
					])}
					onClick={() => fileInputRef.current?.click()}
					onDragOver={(e) => {
						e.preventDefault();
						setDragging(true);
					}}
					onDragLeave={() => setDragging(false)}
					onDrop={(e) => {
						e.preventDefault();
						setDragging(false);
						onFilePicked(e.dataTransfer.files?.[0]);
					}}
				>
					{uploading ? (
						<Loader />
					) : (
						<>
							<SvgIcon
								component={uploadCloud}
								viewBox="0 0 48 48"
								className={classNames([
									styles['dropzone-icon'],
									styles['no-fill'],
								])}
							/>
							<div className={styles['dropzone-label']}>
								Select a CSV to upload
							</div>
							<div className={styles['dropzone-hint']}>
								or drag and drop here
							</div>
						</>
					)}
				</div>
			)}

			<input
				ref={fileInputRef}
				className={styles['hidden-input']}
				type="file"
				accept=".csv,text/csv"
				onChange={(e) => {
					onFilePicked(e.target.files?.[0]);
					e.target.value = '';
				}}
			/>
		</div>
	);

	const renderMappingSection = (
		section: 'required' | 'recommended' | 'optional',
		label: string,
	) => {
		const fields = FIELD_META.filter((f) => f.section === section);
		return (
			<div className={styles['mapping-section']}>
				<div className={styles['section-label']}>{label}</div>
				<div className={styles['mapping-card']}>
					{fields.map((field) => {
						const mapped = aliases[field.key];
						const badgeClass = mapped
							? styles['found']
							: section === 'required'
							? styles['missing-required']
							: styles['missing-optional'];
						return (
							<button
								key={field.key}
								type="button"
								className={styles['mapping-row']}
								onClick={(e) => {
									setMappingMenu({
										key: field.key,
										anchor: e.currentTarget,
									});
								}}
							>
								<span className={styles['field-name']}>
									{field.label}
								</span>
								<span
									className={classNames([
										styles['badge'],
										badgeClass,
									])}
								>
									{mapped || 'Not found'}
								</span>
							</button>
						);
					})}
				</div>
			</div>
		);
	};

	const renderMappingStep = () => (
		<div className={styles['step']}>
			<div className={styles['heading-block']}>
				<div className={styles['title']}>Match properties</div>
				<div className={styles['subtitle']}>
					These fields will be used to identify existing songs or to
					create new songs. Select an option to change.
				</div>
			</div>
			{renderMappingSection('required', 'Required')}
			{renderMappingSection('recommended', 'Recommended')}
			{renderMappingSection('optional', 'Optional')}
		</div>
	);

	const renderSummaryStep = () => {
		const sample = report?.sample?.[0];
		const sampleGradient =
			sample && !sample.artworkUrl
				? generateAlbumGradient(sample.title)
				: null;
		const matchedCount = report?.matchedSongIds.length ?? 0;
		const newCount = report?.newSongs.length ?? 0;
		const existingCount = report?.existingOnTeam.length ?? 0;
		const incompleteCount = report?.missingLyrics.length ?? 0;
		const totalFoundCount = report?.totalSongs ?? 0;
		const notImportingCount = existingCount + incompleteCount;

		return (
			<div className={styles['step']}>
				<div className={styles['heading-block']}>
					<div className={styles['title']}>Import songs</div>
					<div className={styles['subtitle']}>
						{totalFoundCount} song
						{totalFoundCount === 1 ? '' : 's'} processed from your
						CSV. {importableCount} will be imported.{' '}
						{notImportingCount} will be skipped. Review details
						before importing.
					</div>
				</div>

				{sample && (
					<div className={styles['preview-section']}>
						<div className={styles['section-label']}>
							First song preview
						</div>
						<div className={styles['summary-card']}>
							{sample.artworkUrl ? (
								<img
									className={styles['thumb']}
									src={sample.artworkUrl}
									alt=""
								/>
							) : (
								<div
									className={styles['thumb']}
									style={sampleGradient?.style}
								>
									<span
										style={{
											color: sampleGradient?.textColor,
										}}
									>
										{getAcronym(sample.title)}
									</span>
								</div>
							)}
							<div className={styles['meta']}>
								<div className={styles['song-title']}>
									{sample.title}
								</div>
								<div className={styles['artist']}>
									{sample.artist}
								</div>
							</div>
							{sample.defaultKey && (
								<div className={styles['key-badge']}>
									{sample.defaultKey}
								</div>
							)}
						</div>
					</div>
				)}

				<div className={styles['stats-section']}>
					<div className={styles['section-label']}>Importing</div>
					<div className={styles['stats-card']}>
						<button
							type="button"
							className={classNames([
								styles['stat-row'],
								matchedCount > 0 && styles['clickable'],
							])}
							onClick={openMatchedSongs}
							disabled={!matchedCount}
						>
							<div className={styles['label-block']}>
								<div className={styles['label']}>
									Matched songs
								</div>
								<div className={styles['hint']}>
									Found in the Asaph library
								</div>
							</div>
							<span
								className={classNames([
									styles['count'],
									styles['green'],
								])}
							>
								{matchedCount}
							</span>
						</button>
						<button
							type="button"
							className={classNames([
								styles['stat-row'],
								newCount > 0 && styles['clickable'],
							])}
							onClick={() =>
								openListCard(
									'Create new songs',
									report?.newSongs || [],
									'new',
								)
							}
							disabled={!newCount}
						>
							<div className={styles['label-block']}>
								<div className={styles['label']}>
									Create new songs
								</div>
								<div className={styles['hint']}>
									Will be added to your library
								</div>
							</div>
							<span
								className={classNames([
									styles['count'],
									styles['blue'],
								])}
							>
								{newCount}
							</span>
						</button>
					</div>
				</div>

				<div className={styles['stats-section']}>
					<div className={styles['section-label']}>Not importing</div>
					<div className={styles['stats-card']}>
						<button
							type="button"
							className={classNames([
								styles['stat-row'],
								existingCount > 0 && styles['clickable'],
							])}
							onClick={() =>
								openListCard(
									'Existing songs',
									report?.existingOnTeam || [],
									'existing',
								)
							}
							disabled={!existingCount}
						>
							<div className={styles['label-block']}>
								<div className={styles['label']}>
									Existing songs
								</div>
								<div className={styles['hint']}>
									Found in your song library
								</div>
							</div>
							<span
								className={classNames([
									styles['count'],
									styles['yellow'],
								])}
							>
								{existingCount}
							</span>
						</button>
						{incompleteCount > 0 && (
							<button
								type="button"
								className={classNames([
									styles['stat-row'],
									styles['clickable'],
								])}
								onClick={() =>
									openListCard(
										'Incomplete songs',
										report?.missingLyrics || [],
										'missing',
									)
								}
							>
								<div className={styles['label-block']}>
									<div className={styles['label']}>
										Incomplete songs
									</div>
									<div className={styles['hint']}>
										Songs with missing data
									</div>
								</div>
								<span
									className={classNames([
										styles['count'],
										styles['red'],
									])}
								>
									{incompleteCount}
								</span>
							</button>
						)}
					</div>
				</div>
			</div>
		);
	};

	const cancellingImport =
		!!importProgress?.cancelledAt && !importProgress?.completedAt;

	const renderProgressStep = () => (
		<div className={styles['progress-state']}>
			<CircularProgress size={48} thickness={3} />
			<div className={styles['title']}>
				{cancellingImport
					? 'Cancelling import…'
					: `Importing ${
							importProgress?.totalSongs ?? importableCount
					  } songs...`}
			</div>
			<div className={styles['subtitle']}>
				{importProgress
					? `${importProgress.progress}% complete`
					: 'Status updates'}
			</div>
			{importProgress?.error && (
				<div className={styles['error']}>{importProgress.error}</div>
			)}
		</div>
	);

	const renderDoneStep = () => {
		const importedCount =
			(importProgress?.matchedSongs || 0) +
			(importProgress?.createdSongs || 0);

		return (
			<div className={styles['progress-state']}>
				<SvgIcon
					component={checkCircle}
					viewBox="0 0 20 20"
					className={classNames([
						styles['state-icon'],
						styles['no-fill'],
					])}
				/>
				<div className={styles['title']}>
					{importProgress?.cancelledAt
						? `Import cancelled — ${importedCount} song${
								importedCount === 1 ? '' : 's'
						  } imported`
						: `Imported ${importedCount} songs`}
				</div>
				{importProgress?.error && (
					<div className={styles['error']}>{importProgress.error}</div>
				)}
			</div>
		);
	};

	const renderStepBody = () => {
		switch (step) {
			case 0:
				return renderUploadStep();
			case 1:
				return renderMappingStep();
			case 2:
				return renderSummaryStep();
			case 3:
				return renderProgressStep();
			case 4:
				return renderDoneStep();
			default:
				return null;
		}
	};

	const renderActions = () => {
		if (step === 0) {
			return (
				<AsaphButton
					color={
						file && uploadKey && !uploading ? 'blue' : 'inactive'
					}
					fullWidth
					label="Continue"
					disabled={!file || !uploadKey || uploading}
					onClick={() => setStep(1)}
				/>
			);
		}
		if (step === 1) {
			const verifyProgress =
				verifyJobActive && importProgress?.phase === 'verify'
					? importProgress.progress
					: null;
			return (
				<div className={styles['action-stack']}>
					{verifying && (
						<div className={styles['verify-status']}>
							<div>
								Checking {rowCount} song
								{rowCount === 1 ? '' : 's'}
								{verifyProgress != null
									? ` — ${verifyProgress}% complete`
									: '…'}
							</div>
							<div className={styles['verify-status-warning']}>
								You can cancel anytime; matching stops on the
								server when you do.
							</div>
						</div>
					)}
					<div className={styles['actions-row']}>
						<AsaphButton
							color="grey"
							fullWidth
							label="Back"
							disabled={verifying}
							onClick={() => setStep(0)}
						/>
						<AsaphButton
							color="blue"
							fullWidth
							label={verifying ? 'Processing…' : 'Next'}
							isLoading={verifying}
							disabled={!canContinueMapping || verifying}
							onClick={() => void runVerify()}
						/>
					</div>
				</div>
			);
		}
		if (step === 2) {
			return (
				<div className={styles['actions-row']}>
					<AsaphButton
						color="grey"
						fullWidth
						label="Back"
						onClick={() => setStep(1)}
					/>
					<AsaphButton
						color="blue"
						fullWidth
						label={`Import ${importableCount} song${
							importableCount === 1 ? '' : 's'
						}`}
						disabled={importableCount === 0 || processing}
						onClick={() => void startImport()}
					/>
				</div>
			);
		}
		if (step === 3) {
			return (
				<AsaphButton
					color="inactive"
					fullWidth
					label="Importing"
					disabled
				/>
			);
		}
		return (
			<div className={styles['actions-row']}>
				<AsaphButton
					color="grey"
					fullWidth
					label="All Songs"
					onClick={goSongs}
				/>
				<AsaphButton
					color="blue"
					fullWidth
					label="Dashboard"
					onClick={goDashboard}
				/>
			</div>
		);
	};

	return (
		<>
			<AsaphDialog
				open={true}
				onClose={() => {
					if (step < 4) {
						handleClose();
					}
				}}
				paperClassName={styles['panel']}
			>
				<div className={styles['toolbar']}>
					<AsaphDialogHeader
						leadingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component:
								step >= 4 ? null : (
									<AsaphDialogHeaderButton
										type="text"
										label="Cancel"
										handlePress={handleClose}
									/>
								),
						}}
						title={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							label: 'Import from CSV',
						}}
						trailingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component: '',
						}}
					/>
				</div>
				<section className={styles['content']}>
					<div className={styles['top']}>
						{step < 3 ? (
							<IndexIndicator
								className={styles['indicator']}
								dotClassName={styles['indicator-dot']}
								activeClassName={styles['indicator-active']}
								total={FLOW_STEPS}
								current={step}
								variant="stretched"
								onIndicatorClick={(i) => {
									if (i < step && step < 3) {
										setStep(i);
									}
								}}
								hasHover={step < 3}
							/>
						) : (
							<div className={styles['progress-track']}>
								<div
									className={styles['progress-fill']}
									style={{ width: `${progressPercent}%` }}
								/>
							</div>
						)}
					</div>
					<div className={styles['bottom']}>
						<div className={styles['bottom-wrapper']}>
							<BottomActionLayout
								actionDivider
								actionContent={renderActions()}
							>
								{renderStepBody()}
							</BottomActionLayout>
						</div>
					</div>
				</section>
			</AsaphDialog>

			<AsaphDropdownMenu
				open={!!mappingMenu}
				anchorEl={mappingMenu?.anchor || null}
				onClose={() => setMappingMenu(null)}
				selectable
				items={columnSelectItems.map((item) => ({
					label: item.label,
					value: item.value,
					selected:
						(aliases[mappingMenu?.key || 'title'] || '') ===
						item.value,
					onClick: () => {
						if (!mappingMenu) return;
						setAliases((prev) => ({
							...prev,
							[mappingMenu.key]: item.value || null,
						}));
						setMappingMenu(null);
					},
				}))}
			/>

			{matchedViewerIds && (
				<ImportMatchedSongs
					key={matchedViewerIds.join(',')}
					songIds={matchedViewerIds}
					onClose={() => setMatchedViewerIds(null)}
					onSongOpen={(songId) => openSong({ songId })}
				/>
			)}
		</>
	);
};

export default ImportCsv;
